import styles from "../LightHouse.module.css"
import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import $ from "jquery";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Chart.js 등록
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// 월별 꺾은선 차트 컴포넌트 (12개월)
function YearlyChart({ chartData }) {
    const [activePoint, setActivePoint] = useState(null);

    if (!chartData || !chartData.data) return null;

    const data = chartData.data;
    const title = chartData.labels?.[0] || '2026년 월별 운세 흐름';
    const width = 600;
    const height = 200;
    const paddingLeft = 25;
    const paddingRight = 25;
    const paddingTop = 30;
    const paddingBottom = 35;
    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    const maxValue = 100;
    const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
    const gradientId = 'areaGradientYearly';

    // 포인트 좌표 계산
    const points = data.map((value, i) => {
        const x = paddingLeft + (i / (data.length - 1)) * chartWidth;
        const y = paddingTop + chartHeight - (value / maxValue) * chartHeight;
        return { x, y, value };
    });

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - paddingBottom} L ${paddingLeft} ${height - paddingBottom} Z`;

    const handlePointInteraction = (index) => {
        setActivePoint(index);
    };

    const handlePointLeave = () => {
        setActivePoint(null);
    };

    return (
        <div className={styles.lineChart}>
            <h4 className={styles.lineChartTitle}>{title}</h4>
            <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
                {/* 배경 가로선 */}
                {[0, 25, 50, 75, 100].map((percent, i) => {
                    const y = paddingTop + chartHeight - (percent / 100) * chartHeight;
                    return (
                        <line
                            key={i}
                            x1={paddingLeft}
                            y1={y}
                            x2={width - paddingRight}
                            y2={y}
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="1"
                        />
                    );
                })}

                {/* 영역 (그라데이션) */}
                <defs>
                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#87CEEB" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#87CEEB" stopOpacity="0.05" />
                    </linearGradient>
                </defs>
                <path d={areaPath} fill={`url(#${gradientId})`} />

                {/* 선 */}
                <path
                    d={linePath}
                    fill="none"
                    stroke="#87CEEB"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* 포인트 + 툴팁 */}
                {points.map((p, i) => (
                    <g key={i}>
                        {/* 툴팁 (활성화된 포인트만) */}
                        {activePoint === i && (
                            <>
                                <rect
                                    x={p.x - 48}
                                    y={p.y - 48}
                                    width="96"
                                    height="36"
                                    rx="6"
                                    fill="rgba(0,0,0,0.9)"
                                />
                                <text
                                    x={p.x}
                                    y={p.y - 24}
                                    textAnchor="middle"
                                    fill="#fff"
                                    fontSize="18"
                                    fontWeight="bold"
                                >
                                    {months[i]} {p.value}점
                                </text>
                            </>
                        )}
                        {/* 포인트 (터치 영역 확대) */}
                        <circle
                            cx={p.x}
                            cy={p.y}
                            r="12"
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => handlePointInteraction(i)}
                            onMouseLeave={handlePointLeave}
                            onClick={() => handlePointInteraction(activePoint === i ? null : i)}
                        />
                        {/* 실제 포인트 */}
                        <circle
                            cx={p.x}
                            cy={p.y}
                            r={activePoint === i ? 6 : 4}
                            fill={activePoint === i ? '#FFD700' : '#87CEEB'}
                            stroke="#fff"
                            strokeWidth="2"
                            style={{ pointerEvents: 'none', transition: 'r 0.15s, fill 0.15s' }}
                        />
                    </g>
                ))}

                {/* 월 라벨 */}
                {months.map((month, i) => (
                    <text
                        key={i}
                        x={points[i]?.x || 0}
                        y={height - 10}
                        textAnchor="middle"
                        fill="rgba(255,255,255,0.8)"
                        fontSize="11"
                    >
                        {month}
                    </text>
                ))}
            </svg>
        </div>
    );
}

// 레이더 차트 컴포넌트
function RadarChart({ graphData }) {
    if (!graphData || !graphData.labels || !graphData.data) return null;

    const size = 260;
    const center = size / 2;
    const maxValue = 100;
    const levels = 5; // 격자 레벨 수
    const chartRadius = 70; // 실제 차트 반경

    // 각도 계산 (5각형 = 72도씩)
    const angleSlice = (Math.PI * 2) / graphData.labels.length;

    // 좌표 계산 함수
    const getX = (value, index) => {
        const angle = angleSlice * index - Math.PI / 2;
        return center + (value / maxValue) * chartRadius * Math.cos(angle);
    };
    const getY = (value, index) => {
        const angle = angleSlice * index - Math.PI / 2;
        return center + (value / maxValue) * chartRadius * Math.sin(angle);
    };

    // 데이터 포인트 경로
    const dataPoints = graphData.data.map((value, i) => `${getX(value, i)},${getY(value, i)}`).join(' ');

    return (
        <div className={styles.radarChart}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {/* 배경 격자 */}
                {[...Array(levels)].map((_, level) => {
                    const levelRadius = ((level + 1) / levels) * chartRadius;
                    const points = graphData.labels.map((_, i) => {
                        const angle = angleSlice * i - Math.PI / 2;
                        const x = center + levelRadius * Math.cos(angle);
                        const y = center + levelRadius * Math.sin(angle);
                        return `${x},${y}`;
                    }).join(' ');
                    return (
                        <polygon
                            key={level}
                            points={points}
                            fill="none"
                            stroke="rgba(255,255,255,0.2)"
                            strokeWidth="1"
                        />
                    );
                })}

                {/* 축 선 */}
                {graphData.labels.map((_, i) => {
                    const angle = angleSlice * i - Math.PI / 2;
                    return (
                        <line
                            key={i}
                            x1={center}
                            y1={center}
                            x2={center + chartRadius * Math.cos(angle)}
                            y2={center + chartRadius * Math.sin(angle)}
                            stroke="rgba(255,255,255,0.2)"
                            strokeWidth="1"
                        />
                    );
                })}

                {/* 데이터 영역 */}
                <polygon
                    points={dataPoints}
                    fill="rgba(135, 206, 250, 0.4)"
                    stroke="#87CEEB"
                    strokeWidth="2"
                />

                {/* 데이터 포인트 */}
                {graphData.data.map((value, i) => (
                    <circle
                        key={i}
                        cx={getX(value, i)}
                        cy={getY(value, i)}
                        r="4"
                        fill="#87CEEB"
                        stroke="#fff"
                        strokeWidth="1"
                    />
                ))}

                {/* 라벨 - SVG 내부 */}
                {graphData.labels.map((label, i) => {
                    const angle = angleSlice * i - Math.PI / 2;
                    const labelRadius = chartRadius + 25;
                    const x = center + labelRadius * Math.cos(angle);
                    const y = center + labelRadius * Math.sin(angle);
                    return (
                        <g key={`label-${i}`}>
                            <text
                                x={x}
                                y={y - 5}
                                textAnchor="middle"
                                fill="rgba(255,255,255,0.9)"
                                fontSize="12"
                            >
                                {label}
                            </text>
                            <text
                                x={x}
                                y={y + 10}
                                textAnchor="middle"
                                fill="#87CEEB"
                                fontSize="14"
                                fontWeight="bold"
                            >
                                {graphData.data[i]}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

// 평생운세 가로 막대그래프 컴포넌트 (Chart.js)
function LifetimeBarChart({ graphData }) {
    if (!graphData || !graphData.labels || !graphData.data) return null;

    const data = {
        labels: graphData.labels,
        datasets: [
            {
                data: graphData.data,
                backgroundColor: [
                    'rgba(255, 215, 0, 0.8)',   // 재물 - 골드
                    'rgba(147, 112, 219, 0.8)', // 명예 - 보라
                    'rgba(50, 205, 50, 0.8)',   // 건강 - 초록
                    'rgba(255, 182, 193, 0.8)', // 가정 - 핑크
                    'rgba(135, 206, 235, 0.8)', // 인복 - 하늘
                ],
                borderColor: [
                    'rgba(255, 215, 0, 1)',
                    'rgba(147, 112, 219, 1)',
                    'rgba(50, 205, 50, 1)',
                    'rgba(255, 182, 193, 1)',
                    'rgba(135, 206, 235, 1)',
                ],
                borderWidth: 2,
                borderRadius: 8,
                barThickness: 28,
            }
        ]
    };

    const options = {
        indexAxis: 'y', // 가로 막대그래프
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 13 },
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    label: function(context) {
                        return `${context.parsed.x}점`;
                    }
                }
            }
        },
        scales: {
            x: {
                min: 0,
                max: 100,
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.7)',
                    font: { size: 12 },
                    stepSize: 20,
                    callback: function(value) {
                        return value + '점';
                    }
                }
            },
            y: {
                grid: {
                    display: false
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.9)',
                    font: { size: 14, weight: 'bold' },
                    padding: 10
                }
            }
        },
        animation: {
            duration: 1500,
            easing: 'easeOutQuart'
        }
    };

    return (
        <div className={styles.lifetimeBarChart}>
            <h4 className={styles.lifetimeBarChartTitle}>📊 평생 운세 지수</h4>
            <div className={styles.lifetimeBarChartContainer}>
                <Bar data={data} options={options} />
            </div>
        </div>
    );
}

// 계절별 월별운세 컴포넌트 (신년운세 전용) - GSAP 애니메이션
function SeasonalMonthlyFortune({ html, styles }) {
    const containerRef = useRef(null);
    const seasonRefs = useRef([]);

    // 월별 데이터를 파싱
    const parseMonthlyContent = (htmlContent) => {
        const months = [];

        // 전처리: 줄바꿈, 탭, 연속 공백 정리
        const cleanedHtml = htmlContent
            .replace(/[\n\r\t]+/g, ' ')  // 줄바꿈, 탭 → 공백
            .replace(/\s+/g, ' ')         // 연속 공백 → 단일 공백
            .replace(/>\s+</g, '><')      // 태그 사이 공백 제거
            .trim();

        const patterns = [
            // <h3>10월</h3>, <h3>■ 10월</h3>, <h3>🌸 10월 운세</h3> 등 (숫자 앞 특수문자/이모지/공백 허용)
            /<h3[^>]*>[^<]*?(\d{1,2})\s*월[^<]*<\/h3>([\s\S]*?)(?=<h3[^>]*>[^<]*?\d{1,2}\s*월|$)/gi,
            // <p><strong>10월</strong></p> 형식
            /<p><strong>[^<]*?(\d{1,2})\s*월[^<]*<\/strong><\/p>([\s\S]*?)(?=<p><strong>[^<]*?\d{1,2}\s*월|$)/gi,
            // <strong>10월</strong> 형식 (단독)
            /<strong>[^<]*?(\d{1,2})\s*월[^<]*<\/strong>([\s\S]*?)(?=<strong>[^<]*?\d{1,2}\s*월|$)/gi,
            // <h4>10월</h4> 형식
            /<h4[^>]*>[^<]*?(\d{1,2})\s*월[^<]*<\/h4>([\s\S]*?)(?=<h4[^>]*>[^<]*?\d{1,2}\s*월|$)/gi
        ];

        // 전처리된 HTML과 원본 HTML 모두 시도
        const htmlVersions = [cleanedHtml, htmlContent];

        for (const html of htmlVersions) {
            for (const regex of patterns) {
                let match;
                const tempMonths = [];
                regex.lastIndex = 0; // 정규식 초기화

                while ((match = regex.exec(html)) !== null) {
                    const monthNum = parseInt(match[1]);
                    // 중복 방지
                    if (!tempMonths.find(m => m.month === monthNum)) {
                        tempMonths.push({
                            month: monthNum,
                            content: match[2].trim()
                        });
                    }
                }

                // 12개월 모두 찾았으면 반환
                if (tempMonths.length === 12) {
                    return tempMonths.sort((a, b) => a.month - b.month);
                }
            }
        }

        return months;
    };

    const months = parseMonthlyContent(html);

    // GSAP ScrollTrigger 애니메이션
    useLayoutEffect(() => {
        if (months.length !== 12 || !containerRef.current) return;

        gsap.registerPlugin(ScrollTrigger);

        const ctx = gsap.context(() => {
            // 각 계절 박스 애니메이션
            seasonRefs.current.forEach((seasonBox, idx) => {
                if (!seasonBox) return;

                const header = seasonBox.querySelector(`.${styles.seasonHeader}`);
                const icon = seasonBox.querySelector(`.${styles.seasonIcon}`);
                const monthItems = seasonBox.querySelectorAll(`.${styles.monthItem}`);
                const animationContainer = seasonBox.querySelector(`.${styles.seasonAnimation}`);
                const particles = animationContainer ? animationContainer.querySelectorAll('span') : [];

                // 타임라인 생성 (한 번만 실행)
                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: seasonBox,
                        start: "top 85%",
                        once: true,
                    }
                });

                // 박스 등장
                tl.fromTo(seasonBox,
                    {
                        opacity: 0,
                        y: 80,
                        scale: 0.95
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        ease: "power3.out"
                    }
                );

                // 아이콘 bounce
                tl.fromTo(icon,
                    { scale: 0, rotation: -180 },
                    {
                        scale: 1,
                        rotation: 0,
                        duration: 0.6,
                        ease: "back.out(1.7)"
                    },
                    "-=0.4"
                );

                // 헤더 텍스트 slide in
                tl.fromTo(header,
                    { opacity: 0, x: -30 },
                    {
                        opacity: 1,
                        x: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    },
                    "-=0.3"
                );

                // 월별 아이템 stagger
                tl.fromTo(monthItems,
                    {
                        opacity: 0,
                        y: 40,
                        scale: 0.9
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.5,
                        stagger: 0.15,
                        ease: "power2.out"
                    },
                    "-=0.2"
                );

                // 계절별 파티클 애니메이션
                const seasonType = idx; // 0: 봄, 1: 여름, 2: 가을, 3: 겨울
                const boxHeight = seasonBox.offsetHeight;

                particles.forEach((particle, pIdx) => {
                    const randomDelay = pIdx * 0.3;
                    const startX = gsap.utils.random(5, 95);
                    const duration = gsap.utils.random(5, 8);

                    // 초기 위치 설정 (CSS로 left 고정, y로 이동)
                    particle.style.left = startX + '%';
                    particle.style.top = '0px';
                    particle.style.opacity = '0.8';

                    if (seasonType === 0) {
                        // 봄 - 꽃잎이 흩날리며 내림
                        gsap.fromTo(particle,
                            { y: -50, rotation: 0, opacity: 0.8 },
                            {
                                y: boxHeight + 50,
                                rotation: 360,
                                duration: duration,
                                delay: randomDelay,
                                repeat: -1,
                                ease: "none"
                            }
                        );
                        gsap.to(particle, {
                            x: gsap.utils.random(-30, 30),
                            duration: 2,
                            repeat: -1,
                            yoyo: true,
                            ease: "sine.inOut"
                        });

                    } else if (seasonType === 1) {
                        // 봄 - 꽃잎(낙엽 스타일)이 회전하며 떨어짐
                        gsap.fromTo(particle,
                            { y: -50, rotation: 0, opacity: 0.75 },
                            {
                                y: boxHeight + 50,
                                rotation: 540,
                                duration: duration + 2,
                                delay: randomDelay,
                                repeat: -1,
                                ease: "none"
                            }
                        );
                        gsap.to(particle, {
                            x: gsap.utils.random(-50, 50),
                            duration: 2.5,
                            repeat: -1,
                            yoyo: true,
                            ease: "sine.inOut"
                        });

                    } else if (seasonType === 2) {
                        // 여름 - 햇살 반짝반짝
                        particle.style.top = gsap.utils.random(10, 80) + '%';
                        gsap.fromTo(particle,
                            { scale: 0.5, opacity: 0 },
                            {
                                scale: gsap.utils.random(1.2, 1.8),
                                opacity: gsap.utils.random(0.5, 0.9),
                                duration: gsap.utils.random(1, 2.5),
                                delay: randomDelay,
                                repeat: -1,
                                yoyo: true,
                                ease: "power1.inOut"
                            }
                        );

                    } else {
                        // 겨울 - 눈이 천천히 내림
                        gsap.fromTo(particle,
                            { y: -30, rotation: 0, opacity: 0.9 },
                            {
                                y: boxHeight + 30,
                                rotation: 180,
                                duration: duration + 4,
                                delay: randomDelay,
                                repeat: -1,
                                ease: "none"
                            }
                        );
                        gsap.to(particle, {
                            x: gsap.utils.random(-20, 20),
                            duration: 3,
                            repeat: -1,
                            yoyo: true,
                            ease: "sine.inOut"
                        });
                    }
                });
            });

        }, containerRef);

        return () => ctx.revert();
    }, [months.length, styles]);

    // 월이 12개가 아니면 기존 방식으로 렌더링
    if (months.length !== 12) {
        return <div className="unseResultText" dangerouslySetInnerHTML={{__html: html}} />;
    }

    // 분기별 그룹화 (1월~12월 순서)
    const seasons = [
        { name: '겨울', icon: '❄️', months: [1, 2], class: styles.seasonWinter, desc: '성찰의 계절' },
        { name: '봄', icon: '🌸', months: [3, 4, 5], class: styles.seasonSpring, desc: '새로운 시작의 계절' },
        { name: '여름', icon: '☀️', months: [6, 7, 8], class: styles.seasonSummer, desc: '열정의 계절' },
        { name: '가을', icon: '🍂', months: [9, 10, 11], class: styles.seasonAutumn, desc: '결실의 계절' },
        { name: '겨울', icon: '❄️', months: [12], class: styles.seasonWinter, desc: '성찰의 계절' }
    ];

    return (
        <div className={styles.seasonalMonthlyContainer} ref={containerRef}>
            {seasons.map((season, idx) => {
                const seasonMonths = months
                    .filter(m => season.months.includes(m.month))
                    .sort((a, b) => season.months.indexOf(a.month) - season.months.indexOf(b.month));

                return (
                    <div
                        key={idx}
                        className={`${styles.seasonalMonthlyBox} ${season.class}`}
                        ref={el => seasonRefs.current[idx] = el}
                    >
                        {/* 파티클 배경 */}
                        <div className={styles.seasonAnimation}>
                            {[...Array(12)].map((_, i) => (
                                <span key={i} className={styles.seasonParticle}>{season.icon}</span>
                            ))}
                        </div>

                        {/* 계절 헤더 */}
                        <div className={styles.seasonHeader}>
                            <span className={styles.seasonIcon}>{season.icon}</span>
                            <div className={styles.seasonInfo}>
                                <h4 className={styles.seasonTitle}>{season.name} ({season.months.length === 1 ? `${season.months[0]}월` : `${season.months[0]}~${season.months[season.months.length - 1]}월`})</h4>
                                <p className={styles.seasonDesc}>{season.desc}</p>
                            </div>
                        </div>

                        {/* 월별 내용 */}
                        <div className={styles.seasonContent}>
                            {seasonMonths.map((m, mIdx) => (
                                <div key={mIdx} className={styles.monthItem}>
                                    <div className={styles.monthHeader}>
                                        <span className={styles.monthBadge}>{m.month}월</span>
                                    </div>
                                    <div
                                        className={styles.monthContent}
                                        dangerouslySetInnerHTML={{__html: m.content}}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// 원형 게이지 차트 컴포넌트 (total_score용)
function CircularGauge({ scoreData }) {
    const [animatedScore, setAnimatedScore] = useState(0);

    const label = scoreData?.labels?.[0] || '운세';
    const score = scoreData?.data?.[0] || 0;

    const size = 200;
    const strokeWidth = 12;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = (animatedScore / 100) * circumference;
    const dashOffset = circumference - progress;

    // 점수에 따른 색상
    const getScoreColor = (s) => {
        if (s >= 80) return '#4CAF50'; // 초록
        if (s >= 60) return '#87CEEB'; // 하늘
        if (s >= 40) return '#FFD700'; // 노랑
        return '#FF6B6B'; // 빨강
    };

    const color = getScoreColor(score);

    // 점수에 따른 메시지
    const getScoreMessage = (s) => {
        if (s >= 90) return '최고의 운세!';
        if (s >= 80) return '아주 좋아요!';
        if (s >= 70) return '좋은 편이에요';
        if (s >= 60) return '괜찮아요';
        if (s >= 50) return '보통이에요';
        if (s >= 40) return '조금 주의';
        return '신중하게!';
    };

    // 애니메이션
    useEffect(() => {
        if (!scoreData?.data?.[0]) return;

        const duration = 1500;
        const startTime = Date.now();

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const prog = Math.min(elapsed / duration, 1);

            // easeOutExpo
            const easeProgress = prog === 1 ? 1 : 1 - Math.pow(2, -10 * prog);
            setAnimatedScore(Math.round(score * easeProgress));

            if (prog < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }, [score, scoreData]);

    if (!scoreData || !scoreData.labels || !scoreData.data) return null;

    return (
        <div className={styles.circularGauge}>
            <div className={styles.gaugeContainer}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    {/* 배경 원 */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth={strokeWidth}
                    />

                    {/* 그라데이션 정의 */}
                    <defs>
                        <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={color} stopOpacity="0.8" />
                            <stop offset="100%" stopColor={color} stopOpacity="1" />
                        </linearGradient>
                        <filter id="glow">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>

                    {/* 진행 원 */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke="url(#gaugeGradient)"
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={dashOffset}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                        filter="url(#glow)"
                        style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
                    />

                    {/* 점수 표시 */}
                    <text
                        x={size / 2 - 20}
                        y={size / 2 + 20}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize="55"
                        fontWeight="bold"
                    > 
                        {animatedScore}
                    </text>
                    <text
                        x={size / 2 + 35}
                        y={size / 2 + 20}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize="55"
                    > 점
                    </text>
                </svg>

                {/* 라벨 */}
                <div className={styles.gaugeLabel}>
                    <span className={styles.gaugeLabelText}>{label}</span>
                    <span className={styles.gaugeMessage} style={{ color }}>
                        {getScoreMessage(score)}
                    </span>
                </div>
            </div>
        </div>
    );
}

// 행운 아이템 컴포넌트
function LuckyItems({ luckyItems }) {
    if (!luckyItems) return null;

    const itemLabels = {
        color: '행운의 색',
        number: '행운의 숫자',
        direction: '행운의 방향'
    };

    return (
        <div className={styles.luckyItems}>
            <h4 className={styles.luckyItemsTitle}>🍀 행운 아이템</h4>
            <ul className={styles.luckyItemsList}>
                {Object.entries(luckyItems).map(([key, value]) => (
                    <li key={key} className={styles.luckyItemsItem}>
                        <span className={styles.luckyItemLabel}>{itemLabels[key] || key}</span>
                        <span className={styles.luckyItemValue}>{value}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function UnseResult({Loading, Constellation }) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const APIKEY = process.env.REACT_APP_UNSE_API_KEY;
    const [searchParams] = useSearchParams();

    // URL 파라미터 또는 localStorage에서 unseLogId 가져오기
    const urlUnseLogId = searchParams.get('id');
    const localUnseLogId = localStorage.getItem('lighthouse_unseLogId');
    const unseLogId = urlUnseLogId || localUnseLogId;

    // 공유 링크(URL 파라미터)로 접속한 경우 member_id 없이 조회
    const isSharedLink = !!urlUnseLogId;
    const memberId = isSharedLink ? null : localStorage.getItem('lighthouse_memberId');

    const [data, setData] = useState([]);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [shortUrl, setShortUrl] = useState(null);
    const siteCode = process.env.REACT_APP_SITE_CODE_LIGHTHOUSE;
    const contentRef = useRef(null);
    const ONURL_API_KEY = 'c488b881b8cc481b741e0703720b8cb61f9b58fe7814484a17026c588dafd0de';

    // URL 단축 함수
    const shortenUrl = async (longUrl) => {
        try {
            const response = await fetch('https://onurl.cc/api/v1/shorten', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': ONURL_API_KEY
                },
                body: JSON.stringify({ url: longUrl })
            });
            const result = await response.json();
            if (result.success && result.data?.short_url) {
                return result.data.short_url;
            }
            return longUrl; // 실패 시 원본 URL 반환
        } catch (err) {
            console.error('URL 단축 에러:', err);
            return longUrl; // 에러 시 원본 URL 반환
        }
    };

    useEffect(() => {
        if(unseLogId) {
            $.ajax({
                url: `${API_BASE_URL}/getUnseResult`,
                type: "POST",
                contentType: "application/json",
                dataType: "json",
                data: JSON.stringify({
                    "member_id": memberId,
                    "unse_log_id": unseLogId,
                    "site_code": siteCode
                }),
                cache: false,
                headers: {
                    APIKEY: APIKEY,
                },
                success: function (res) {
                    if (res.code != "0") {
                        alert(res.msg); return false;
                    }
                    setData(res);
                },
                error: function (xhr, status, err) {
                    console.error("에러:", err);
                },
            });

            // 단축 URL 생성
            const originalUrl = `${window.location.origin}/lighthouse/UnseResult?id=${unseLogId}`;
            shortenUrl(originalUrl).then(url => setShortUrl(url));
        } else {
            alert("잘못된 접근입니다.");
            window.location.href="/lighthouse";
        }

    },[unseLogId]);

    // 공유 URL (단축 URL 우선, 없으면 원본 URL)
    const originalUrl = `${window.location.origin}/lighthouse/UnseResult?id=${unseLogId}`;
    const shareUrl = shortUrl || originalUrl;
    const shareTitle = data.subtitle ? `소원등대 - ${data.subtitle}` : '소원등대 운세';
    const shareText = '나의 운세 결과를 확인해보세요!';

    const handleCopyLink = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shareUrl);
            } else {
                // fallback
                const textArea = document.createElement('textarea');
                textArea.value = shareUrl;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            // fallback
            const textArea = document.createElement('textarea');
            textArea.value = shareUrl;
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    // Flutter 앱 환경인지 확인
    const isFlutterApp = () => {
        const result = window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === 'function';
        console.log('isFlutterApp check:', result, 'flutter_inappwebview:', typeof window.flutter_inappwebview);
        return result;
    };

    // Flutter 네이티브 공유 시도 (재시도 포함)
    const tryNativeShare = async (shareData, maxRetries = 3) => {
        for (let i = 0; i < maxRetries; i++) {
            if (window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === 'function') {
                try {
                    console.log(`네이티브 공유 시도 (${i + 1}/${maxRetries})`);
                    const result = await window.flutter_inappwebview.callHandler('nativeShare', shareData);
                    console.log('네이티브 공유 결과:', result);
                    return { success: true, result };
                } catch (err) {
                    console.error('네이티브 공유 에러:', err);
                    return { success: false, error: err };
                }
            }
            // 100ms 대기 후 재시도
            if (i < maxRetries - 1) {
                console.log(`flutter_inappwebview 대기 중... (${i + 1}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }
        console.log('flutter_inappwebview를 찾을 수 없음');
        return { success: false, notFound: true };
    };

    // 네이티브 공유 (Flutter 앱에서 공유 시트 띄움)
    const handleNativeShare = async () => {
        console.log('네이티브 공유 시도');

        // Flutter 앱 환경일 경우 네이티브 공유 핸들러 호출
        const nativeResult = await tryNativeShare({
            title: shareTitle,
            text: shareText,
            url: shareUrl
        });

        if (nativeResult.success || !nativeResult.notFound) {
            setShowShareMenu(false);
            return;
        }

        // Web Share API 시도
        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: shareUrl,
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('공유 에러:', err);
                }
            }
        } else {
            // Web Share API 미지원 시 링크 복사
            handleCopyLink();
        }
        setShowShareMenu(false);
    };

    // 외부 URL 열기 (앱 환경 대응)
    const openExternalUrl = (url) => {
        // 앱 환경에서는 window.open이 안 될 수 있으므로 location.href 사용
        try {
            const newWindow = window.open(url, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                // 팝업 차단되거나 앱 환경일 경우
                window.location.href = url;
            }
        } catch (e) {
            window.location.href = url;
        }
    };

    const handleKakaoShare = async () => {
        console.log('카카오 공유 시도');

        // Flutter 앱 환경일 경우 네이티브 공유 사용 (카카오톡 선택 가능)
        const nativeResult = await tryNativeShare({
            title: shareTitle,
            text: shareText,
            url: shareUrl
        });

        if (nativeResult.success) {
            setShowShareMenu(false);
            return;
        }

        // 네이티브 공유 실패 시 웹 환경으로 진행
        if (!nativeResult.notFound) {
            // 에러 발생했지만 Flutter 앱 환경이었음 - 그냥 종료
            setShowShareMenu(false);
            return;
        }

        // 웹 환경: Kakao SDK 사용
        console.log('window.Kakao:', window.Kakao);
        console.log('isInitialized:', window.Kakao?.isInitialized?.());

        if (window.Kakao && window.Kakao.isInitialized && window.Kakao.isInitialized()) {
            try {
                window.Kakao.Share.sendDefault({
                    objectType: 'feed',
                    content: {
                        title: shareTitle,
                        description: shareText,
                        imageUrl: 'https://sowonlighthouse.com/image/lightHouse_logo.png',
                        link: {
                            mobileWebUrl: shareUrl,
                            webUrl: shareUrl,
                        },
                    },
                    buttons: [
                        {
                            title: '운세 보러가기',
                            link: {
                                mobileWebUrl: shareUrl,
                                webUrl: shareUrl,
                            },
                        },
                    ],
                });
            } catch (e) {
                console.error('카카오 공유 에러:', e);
                // SDK 에러 시 네이티브 공유 시도
                if (navigator.share) {
                    navigator.share({
                        title: shareTitle,
                        text: shareText,
                        url: shareUrl,
                    }).catch(err => {
                        alert('공유에 실패했습니다. 링크를 복사해주세요.');
                        handleCopyLink();
                    });
                } else {
                    alert('카카오톡 공유가 지원되지 않습니다. 링크를 복사해주세요.');
                    handleCopyLink();
                }
            }
        } else {
            // Kakao SDK 미초기화 시 네이티브 공유 또는 링크 복사
            console.log('Kakao SDK 미초기화 - 네이티브 공유 시도');
            if (navigator.share) {
                navigator.share({
                    title: shareTitle,
                    text: shareText + '\n카카오톡으로 공유해주세요!',
                    url: shareUrl,
                }).catch(err => {
                    if (err.name !== 'AbortError') {
                        alert('링크를 복사했습니다. 카카오톡에서 직접 공유해주세요.');
                        handleCopyLink();
                    }
                });
            } else {
                alert('링크를 복사했습니다. 카카오톡에서 직접 공유해주세요.');
                handleCopyLink();
            }
        }
        setShowShareMenu(false);
    };

    const handleTwitterShare = async () => {
        console.log('X(트위터) 공유 시도');

        // Flutter 앱 환경일 경우 네이티브 공유 사용
        const nativeResult = await tryNativeShare({
            title: shareTitle,
            text: shareText,
            url: shareUrl
        });

        if (nativeResult.success || !nativeResult.notFound) {
            setShowShareMenu(false);
            return;
        }

        // 웹 환경: 트위터 공유 URL
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle + ' - ' + shareText)}&url=${encodeURIComponent(shareUrl)}`;
        openExternalUrl(twitterUrl);
        setShowShareMenu(false);
    };

    const handleInstagramShare = async () => {
        console.log('인스타그램 공유 시도');

        // Flutter 앱 환경일 경우 네이티브 공유 사용
        const nativeResult = await tryNativeShare({
            title: shareTitle,
            text: shareText,
            url: shareUrl
        });

        if (nativeResult.success || !nativeResult.notFound) {
            setShowShareMenu(false);
            return;
        }

        // 웹 환경: 인스타그램은 직접 공유 URL이 없으므로 링크 복사 후 안내
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(shareUrl).then(() => {
                alert('링크가 복사되었습니다!\n인스타그램 앱에서 스토리나 DM으로 공유해주세요.');
            }).catch(() => {
                fallbackCopyToClipboard(shareUrl);
            });
        } else {
            fallbackCopyToClipboard(shareUrl);
        }
        setShowShareMenu(false);
    };

    const fallbackCopyToClipboard = (text) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            alert('링크가 복사되었습니다. 인스타그램 앱에서 스토리나 DM으로 공유해주세요!');
        } catch (err) {
            alert('링크 복사에 실패했습니다. 직접 URL을 복사해주세요.');
        }
        document.body.removeChild(textArea);
    };

    const handleDownloadPDF = () => {
        window.print();
    };

    if (!data) {
        return <Loading Constellation={Constellation}/>;
    }

    return (
        <div className="UnseResultPage_inner">
            <div className={`UnseResultContent ${styles.UnseResultContent_lightHouse}`} ref={contentRef}>
                {/* 헤더 영역 - 사용자 정보 + 버튼들 */}
                <div className={styles.resultHeader}>
                    <div className={styles.resultHeaderInfo}>
                        <h4 className="userInfo">
                            {data.title}
                        </h4>
                        <h3 className={`subTitle ${styles.subTitle_lightHouse}`}>{data.subtitle}</h3>
                    </div>

                    {/* 공유 + 다운로드 버튼 영역 */}
                    <div className={styles.resultButtons}>
                        {/* 공유 버튼 */}
                        <button
                            className={styles.resultBtn}
                            onClick={() => setShowShareMenu(!showShareMenu)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.19071C7.54305 8.46589 6.6582 8 5.66667 8C4.19391 8 3 9.34315 3 11C3 12.6569 4.19391 14 5.66667 14C6.6582 14 7.54305 13.5341 8.08261 12.8093L15.0227 16.6294C15.0077 16.7508 15 16.8745 15 17C15 18.6569 16.3431 20 18 20C19.6569 20 21 18.6569 21 17C21 15.3431 19.6569 14 18 14C17.0085 14 16.1236 14.4659 15.5841 15.1907L8.64394 11.3706C8.65897 11.2492 8.66667 11.1255 8.66667 11C8.66667 10.8745 8.65897 10.7508 8.64394 10.6294L15.5841 6.80929C16.1236 7.53411 17.0085 8 18 8Z" fill="white"/>
                            </svg>
                        </button>

                        {/* 다운로드 버튼 */}
                        <button
                            className={styles.resultBtn}
                            onClick={handleDownloadPDF}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 16L12 8M12 16L8 12M12 16L16 12M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>

                        {/* 공유 메뉴 */}
                        {showShareMenu && (
                            <>
                                <div
                                    onClick={() => setShowShareMenu(false)}
                                    style={{
                                        position: 'fixed',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 9997
                                    }}
                                />
                                <div className={styles.shareMenu}>
                                    <button
                                        className={styles.shareMenuItem}
                                        onClick={handleKakaoShare}
                                        style={{ background: '#FEE500', color: '#000' }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M10 3C5.58172 3 2 5.76903 2 9.16667C2 11.3369 3.48276 13.2355 5.7379 14.3308L4.92683 17.5122C4.86556 17.7529 5.14111 17.9434 5.35006 17.8041L9.21316 15.2321C9.46947 15.2761 9.73218 15.3 10 15.3C14.4183 15.3 18 12.564 18 9.16667C18 5.76903 14.4183 3 10 3Z" fill="#000"/>
                                        </svg>
                                        카카오톡
                                    </button>
                                    <button
                                        className={styles.shareMenuItem}
                                        onClick={handleTwitterShare}
                                        style={{ background: '#000', color: '#fff' }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                        </svg>
                                        X (트위터)
                                    </button>
                                    <button
                                        className={styles.shareMenuItem}
                                        onClick={handleInstagramShare}
                                        style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', color: '#fff' }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                        </svg>
                                        인스타그램
                                    </button>
                                    <button
                                        className={styles.shareMenuItem}
                                        onClick={handleCopyLink}
                                        style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                            <path d="M13.723 18.654l-3.61 3.609c-2.316 2.315-6.063 2.315-8.378 0-2.315-2.316-2.315-6.062 0-8.377l3.61-3.61 1.414 1.415-3.61 3.609c-1.535 1.536-1.535 4.014 0 5.55 1.536 1.535 4.014 1.535 5.55 0l3.61-3.61 1.414 1.414zm-1.446-12.308l3.61-3.61c2.315-2.315 6.062-2.315 8.377 0 2.316 2.316 2.316 6.062 0 8.377l-3.61 3.61-1.414-1.414 3.609-3.61c1.536-1.536 1.536-4.014 0-5.55-1.535-1.535-4.014-1.535-5.55 0l-3.61 3.61-1.412-1.413zm-.707 4.242l5.657 5.657-1.414 1.414-5.657-5.657 1.414-1.414z"/>
                                        </svg>
                                        {copySuccess ? '복사됨!' : '링크 복사'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* 운세 결과 내용 */}
                {(() => {
                    // data.result에서 데이터 추출
                    let parsedResult = null;

                    if (typeof data.result === 'string' && (data.result.includes('"graph_data"') || data.result.includes('"total_score"') || data.result.includes('"total_graph_data"'))) {
                        try {
                            // graph_data 추출
                            const graphMatch = data.result.match(/"graph_data"\s*:\s*(\{[\s\S]*?"data"\s*:\s*\[[^\]]+\]\s*\})/);
                            // monthly_data 추출 (12개월)
                            const monthlyMatch = data.result.match(/"monthly_data"\s*:\s*(\{[\s\S]*?"data"\s*:\s*\[[^\]]+\]\s*\})/);
                            // total_score 추출 (원형 게이지용)
                            const totalScoreMatch = data.result.match(/"total_score"\s*:\s*(\{[\s\S]*?"data"\s*:\s*\[[^\]]+\]\s*\})/);
                            // total_graph_data 추출 (평생운세 가로 막대그래프용)
                            const totalGraphMatch = data.result.match(/"total_graph_data"\s*:\s*(\{[\s\S]*?"data"\s*:\s*\[[^\]]+\]\s*\})/);
                            // lucky_items 추출
                            const luckyMatch = data.result.match(/"lucky_items"\s*:\s*(\{[^}]+\})/);
                            // html 추출 (마지막 큰따옴표와 } 전까지)
                            const htmlMatch = data.result.match(/"html"\s*:\s*"([\s\S]*?)"\s*\}?\s*$/);

                            if (graphMatch || luckyMatch || htmlMatch || monthlyMatch || totalScoreMatch || totalGraphMatch) {
                                parsedResult = {};

                                if (graphMatch) {
                                    try {
                                        parsedResult.graph_data = JSON.parse(graphMatch[1]);
                                    } catch(e) {}
                                }

                                if (monthlyMatch) {
                                    try {
                                        parsedResult.monthly_data = JSON.parse(monthlyMatch[1]);
                                    } catch(e) {}
                                }

                                if (totalScoreMatch) {
                                    try {
                                        parsedResult.total_score = JSON.parse(totalScoreMatch[1]);
                                    } catch(e) {}
                                }

                                if (totalGraphMatch) {
                                    try {
                                        parsedResult.total_graph_data = JSON.parse(totalGraphMatch[1]);
                                    } catch(e) {}
                                }

                                if (luckyMatch) {
                                    try {
                                        parsedResult.lucky_items = JSON.parse(luckyMatch[1]);
                                    } catch(e) {}
                                }

                                if (htmlMatch) {
                                    // html 내용에서 이스케이프된 문자 복원 + 연속 줄바꿈 정리
                                    parsedResult.html = htmlMatch[1]
                                        .replace(/\\n/g, '\n')
                                        .replace(/\\"/g, '"')
                                        .replace(/\\\//g, '/')
                                        .replace(/\n{2,}/g, '\n')  // 연속 줄바꿈 -> 1개로
                                        .replace(/>\s*\n\s*</g, '><')  // 태그 사이 공백 제거
                                        .trim();
                                }
                            }
                        } catch (e) {
                            parsedResult = null;
                        }
                    }

                    // total_graph_data가 있으면 평생운세 형식 (가로 막대그래프 + HTML)
                    if (parsedResult && parsedResult.total_graph_data && parsedResult.html) {
                        return (
                            <>
                                {/* 가로 막대그래프 */}
                                <LifetimeBarChart graphData={parsedResult.total_graph_data} />

                                {/* HTML 내용 */}
                                <div
                                    className="unseResultText"
                                    dangerouslySetInnerHTML={{
                                        __html: parsedResult.html
                                    }}
                                />
                            </>
                        );
                    }

                    // total_score가 있으면 월별 운세 형식 (원형 게이지 + HTML)
                    if (parsedResult && parsedResult.total_score && parsedResult.html) {
                        return (
                            <>
                                {/* 원형 게이지 차트 */}
                                <CircularGauge scoreData={parsedResult.total_score} />

                                {/* HTML 내용 */}
                                <div
                                    className="unseResultText"
                                    dangerouslySetInnerHTML={{
                                        __html: parsedResult.html
                                    }}
                                />

                                {/* 행운 아이템 (있을 경우) */}
                                {parsedResult.lucky_items && (
                                    <LuckyItems luckyItems={parsedResult.lucky_items} />
                                )}
                            </>
                        );
                    }

                    // JSON이고 html 필드가 있으면 신년운세 형식
                    if (parsedResult && parsedResult.html) {
                        const html = parsedResult.html;

                        // 총운풀이 섹션 (세부운 h2 전까지만)
                        const summaryMatch = html.match(/(<h2>2026년\s*총운풀이[\s\S]*?)(?=<h2>2026년\s*세부운|$)/);
                        const summaryHtml = summaryMatch ? summaryMatch[1] : '';

                        // 세부운 섹션 (월별운세 h2 전까지만)
                        const detailMatch = html.match(/(<h2>2026년\s*세부운[\s\S]*?)(?=<h2>2026년\s*월별\s*운세|$)/);
                        const detailHtml = detailMatch ? detailMatch[1] : '';

                        // 월별운세: h2만 (띄어쓰기 유연하게 처리)
                        const monthlyH2Match = html.match(/(<h2>2026년\s*월별\s*운세[^<]*<\/h2>)/);
                        const monthlyH2Html = monthlyH2Match ? monthlyH2Match[1] : '';

                        // 월별운세: h2 다음 내용 전체 (각 월이 <h3>N월</h3><div class='noti_box'>내용</div> 형식)
                        const monthlyContentMatch = html.match(/<h2>2026년\s*월별\s*운세[^<]*<\/h2>([\s\S]*)$/);
                        const monthlyContentHtml = monthlyContentMatch ? monthlyContentMatch[1] : '';

                        // noti_box 마무리 총운 (현재 API 응답에서는 사용 안함)
                        const notiBoxHtml = '';

                        return (
                            <>
                                {/* 총운풀이 */}
                                {summaryHtml && (
                                    <div className="unseResultText" dangerouslySetInnerHTML={{__html: summaryHtml}} />
                                )}

                                {/* 레이더 차트 */}
                                {parsedResult.graph_data && (
                                    <RadarChart graphData={parsedResult.graph_data} />
                                )}

                                {/* 세부운 */}
                                {detailHtml && (
                                    <div className={`unseResultText ${styles.detailFortune2026}`} dangerouslySetInnerHTML={{__html: detailHtml}} />
                                )}

                                {/* 월별운세 h2 제목 */}
                                {monthlyH2Html && (
                                    <div className="unseResultText" dangerouslySetInnerHTML={{__html: monthlyH2Html}} />
                                )}

                                {/* 12개월 꺾은선 차트 */}
                                {parsedResult.monthly_data && (
                                    <YearlyChart chartData={parsedResult.monthly_data} />
                                )}

                                {/* 월별운세 내용 (계절별 분기) */}
                                {monthlyContentHtml && (
                                    <SeasonalMonthlyFortune html={monthlyContentHtml} styles={styles} />
                                )}

                                {/* 마무리 총운 (noti_box) */}
                                {notiBoxHtml && (
                                    <div className="unseResultText" dangerouslySetInnerHTML={{__html: notiBoxHtml}} />
                                )}

                                {/* 행운 아이템 */}
                                {parsedResult.lucky_items && (
                                    <LuckyItems luckyItems={parsedResult.lucky_items} />
                                )}
                            </>
                        );
                    }

                    // 기존 운세 결과 (HTML 문자열)
                    return <div className="unseResultText" dangerouslySetInnerHTML={{__html: data.result}} />;
                })()}
            </div>
        </div>
    )
}
